<template>
    <div class="wrapper flex flex-column w-full h-full min-h-screen">
        <CustomBreadcrumb class="flex align-self-start pt-3 pb-4 pl-3"
            :items="[{ label: 'Profile Settings' }, { label: 'Subscription' }]" />

        <!-- Institute-sponsored members: read-only badge instead of any plan controls -->
        <div v-if="managedByInstitute" class="flex justify-content-center w-full px-4 py-8">
            <div class="managed-badge flex flex-column align-items-center text-center gap-3 p-5 border-round">
                <i class="pi pi-verified text-4xl"></i>
                <span class="H6-Medium">{{ MANAGED_BY_INSTITUTE_TEXT }}</span>
                <span class="Label">You can’t change or cancel a plan that your institute provides.</span>
            </div>
        </div>

        <div v-else class="card flex flex-column justify-center px-4 bg-transparent border-none">

            <!-- Plans list view -->
            <div v-if="currentView.type === 'list'" class="w-full flex flex-column align-items-center">
                <span class="block text-center H6-Medium mt-6">Billing & Plan</span>
                <span class="block text-center Label mt-2">Manage your subscription and payment details</span>

                <div v-if="pageLoading" class="flex align-items-center justify-content-center py-8 gap-3">
                    <ProgressSpinner style="width: 40px; height: 40px" />
                    <span class="text-600">Loading subscription...</span>
                </div>

                <SubscriptionPlans
                    v-else
                    mode="settings"
                    class="py-6 w-full max-w-7xl"
                    :currentPlanId="currentPlanId"
                    :packages="packagesWithStatus"
                    :featureDefinitions="tutorFeatures"
                    :disableActions="actionLoading"
                    @package-action="handlePackageAction"
                />
            </div>

            <!-- Upgrade confirm view -->
            <div v-else-if="currentView.type === 'upgradeConfirm'" class="flex justify-content-center w-full">
                <UpgradeConfirm
                    :targetPackage="currentView.data.package"
                    :nextBillingDate="nextBillingDate"
                    :loading="actionLoading"
                    :hasToken="cachedHasToken"
                    @confirm="handleConfirmChange('upgrade', $event)"
                    @cancel="resetToMain"
                />
            </div>

            <!-- Downgrade confirm view -->
            <div v-else-if="currentView.type === 'downgradeConfirm'" class="flex justify-content-center w-full">
                <DowngradeConfirm
                    :currentPlanName="currentPlanName"
                    :targetPackage="currentView.data.package"
                    :nextBillingDate="nextBillingDate"
                    :loading="actionLoading"
                    @confirm="handleConfirmChange('downgrade')"
                    @cancel="resetToMain"
                />
            </div>

            <!-- Downgrade success view -->
            <div v-else-if="currentView.type === 'downgradeSuccess'" class="flex justify-content-center w-full">
                <DowngradeSuccess
                    :nextBillingDate="nextBillingDate"
                    :targetPackageName="currentView.data?.package?.name || 'Educator'"
                    @continue="resetToMain"
                />
            </div>
        </div>

        <!-- Billing Details Dialog (shown when no card on file) -->
        <!-- allow-promo="false": promo is already handled in UpgradeConfirm -->
        <BillingDetailsDialog
            v-model="showBillingDialog"
            context="paid_subscribe"
            :selected-package="pendingPackage"
            :initial-values="billingInitialValues"
            :allow-promo="false"
            @submit="submitBilling"
        />

        <!-- Progress Dialog -->
        <Dialog
            v-model:visible="showProgressDialog"
            modal
            :closable="false"
            header="Updating your subscription"
            :style="{ width: 'min(420px, 92vw)' }"
        >
            <div class="flex flex-column align-items-center py-4 gap-4">
                <ProgressSpinner style="width: 50px; height: 50px" />
                <p class="text-600 text-center m-0">{{ progressMessage }}</p>
            </div>
        </Dialog>

        <!-- Shown when enrolments exceed the plan being switched to -->
        <PlanChangeBlockedDialog
            v-model:visible="showBlockedDialog"
            :package-name="blockedPackageName"
            :blockers="blockerList"
            manage-label="Manage students"
            @manage="goToEnrolledStudents"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import CustomBreadcrumb from '@/components/CustomBreadcrumb.vue';
import SubscriptionPlans from '@/components/subscriptionPlans/tutorSubscriptionPlans.vue';
import UpgradeConfirm from './UpgradeConfirm.vue';
import DowngradeConfirm from './DowngradeConfirm.vue';
import DowngradeSuccess from './DowngradeSuccess.vue';
import BillingDetailsDialog from '@/components/payments/BillingDetailsForm.vue';
import PlanChangeBlockedDialog from '@/components/settings/PlanChangeBlockedDialog.vue';
import Dialog from 'primevue/dialog';
import ProgressSpinner from 'primevue/progressspinner';
import { useAuthStore } from '@/stores/authStore';
import payments from '@/api/payments';
import subscriptionApi from '@/api/subscription';
import { backendApi } from '@/api/axios';
import { PACKAGES_ENDPOINTS, PAYMENT_ENDPOINTS } from '@/api/endpoints';
import { notifyError, notifyContrast } from '@/utils/notification';
import { MANAGED_BY_INSTITUTE_TEXT, notifyIfManagedByInstitute } from '@/utils/subscriptionGuards';

const PENDING_KEY = 'tutorSettingsPendingPackage';

const authStore = useAuthStore();

// True for institute-sponsored members — their plan is read-only.
const managedByInstitute = ref(false);

// View stack
const viewStack   = ref([{ type: 'list', data: null }]);
const currentView = computed(() => viewStack.value[viewStack.value.length - 1]);

const pushView = (type, data = null) => {
    if (type === 'downgradeSuccess') data = currentView.value.data;
    viewStack.value.push({ type, data });
};

const resetToMain = () => {
    viewStack.value   = [{ type: 'list', data: null }];
    pendingDiscount.value = null;
};

// UI state
const pageLoading        = ref(true);
const actionLoading      = ref(false);
const showBillingDialog  = ref(false);
const showProgressDialog = ref(false);
const showBlockedDialog  = ref(false);
const blockerList        = ref([]);
const blockedPackageName = ref('');

const router = useRouter();
const goToEnrolledStudents = () => {
    showBlockedDialog.value = false;
    router.push('/tutor_dashboard/enrolledStudents');
};
const progressMessage    = ref('');
const pendingDiscount    = ref(null);
const cachedHasToken     = ref(false);

// Subscription state
const currentPlanId  = ref(null);
const nextBillingDate = ref('');
const pendingChange  = ref(null);
const tutorPackages  = ref([]);
const pendingPackage = ref(null);
const pendingAction  = ref('upgrade');

const currentPlanName = computed(() => {
    const pkg = tutorPackages.value.find(p => p.id === currentPlanId.value);
    return pkg ? pkg.name : '';
});

const billingInitialValues = computed(() => {
    const u = authStore.user;
    if (!u) return {};
    return {
        firstName: u.firstName    || '',
        lastName:  u.lastName     || '',
        email:     u.email        || '',
        phone:     u.mobileNumber || '',
        address:   u.address      || '',
        city:      u.city         || '',
    };
});

const packagesWithStatus = computed(() => {
    return tutorPackages.value.map(pkg => {
        const isCurrent = currentPlanId.value === pkg.id;
        const isPending = pendingChange.value?.packageId === pkg.id;
        
        let actionLabel = undefined;
        if (isCurrent) {
            actionLabel = 'Current Plan';
        } else if (isPending) {
            actionLabel = `Activates on ${formatDate(pendingChange.value.activationDate)}`;
        }
        
        return {
            ...pkg,
            disabled: isCurrent || isPending,
            actionLabel,
            isCurrent,
            isPending
        };
    });
});

const tutorFeatures = ref([
    { key: 'listedSubjects',   label: 'Listed subjects' },
    { key: 'enrolledStudents', label: 'Enrolled students' },
    { key: 'notes',            label: 'Notes' },
    { key: 'inAppChat',        label: 'In app chat' },
    { key: 'securePayments',   label: 'Secure payments' },
    { key: 'aiFeatures',       label: 'AI features', info: 'Get access to our AI learning assistant' },
    { key: 'pastPapers',       label: 'Past papers' },
    { key: 'sessions',         label: 'Sessions via platform (per month)' },
]);

// Package mapping
function mapLimitValue(value) {
    return value === null || value === undefined ? 'Unlimited' : value.toString();
}

function mapStringFeature(value) {
    if (value === null || value === false || value === 'none') return false;
    // 'all' means the feature is fully included - show a tick rather than the word "All".
    if (value === true || value === 'all') return true;
    if (typeof value === 'string') return value.charAt(0).toUpperCase() + value.slice(1);
    return value;
}

function mapApiPackage(pkg) {
    return {
        id:          pkg._id,
        name:        pkg.name,
        priceLabel:  pkg.pricing.amount === 0 ? '0' : pkg.pricing.amount.toString(),
        popular:     pkg.isPopular,
        pricingType: pkg.pricing.type,
        features: {
            listedSubjects:   mapLimitValue(pkg.limits?.listedSubjects),
            enrolledStudents: mapLimitValue(pkg.limits?.enrolledStudents),
            notes:            mapLimitValue(pkg.limits?.notes),
            sessions:         mapLimitValue(pkg.limits?.sessionsPerMonth),
            securePayments:   pkg.features?.securePayments   ?? true,
            pastPapers:       pkg.features?.pastPapers       ?? false,
            inAppChat:        pkg.features?.inAppChat        ?? false,
            aiFeatures:       mapStringFeature(pkg.features?.aiFeatures       ?? false),
        },
    };
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// API helpers
async function fetchPackages() {
    try {
        const res = await backendApi.get(PACKAGES_ENDPOINTS.GET_ALL, { params: { userType: 'tutor' } });
        tutorPackages.value = (res.data?.data || []).map(mapApiPackage);
    } catch {
        notifyError('Error', 'Failed to load packages. Please refresh the page.');
    }
}

// Returns null on any error (including 500 "No active subscription found")
async function fetchSubscription() {
    try {
        const res  = await subscriptionApi.getMy();
        const data = res?.data?.data ?? {};
        pendingChange.value = data.pendingChange ?? null;
        managedByInstitute.value = data.managedByInstitute === true;
        return data.subscription ?? null;
    } catch {
        return null;
    }
}

async function checkHasToken() {
    const res = await payments.getMyToken();
    return res?.data?.data?.hasToken === true;
}

async function waitForSubscriptionUpdate(targetPackageId, maxAttempts = 15, intervalMs = 2000) {
    for (let i = 0; i < maxAttempts; i++) {
        try {
            const sub = await fetchSubscription();
            const isNowActive = sub?.status === 'active' && (sub.packageId?._id ?? sub.packageId) === targetPackageId;
            const isNowPending = pendingChange.value?.packageId === targetPackageId;
            
            if (isNowActive || isNowPending) {
                return { sub, pending: pendingChange.value };
            }
        } catch { /* ignore */ }
        await new Promise(r => setTimeout(r, intervalMs));
    }
    return null;
}

function redirectToPayHere(paymentData, preapprove = false) {
    const isSandbox = paymentData.sandbox === 'true' || paymentData.sandbox === true;
    const url = preapprove
        ? (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_PREAPPROVE_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_PREAPPROVE_URL)
        : (isSandbox ? PAYMENT_ENDPOINTS.PAYHERE_SANDBOX_URL : PAYMENT_ENDPOINTS.PAYHERE_LIVE_URL);
    payments.processPayHereRedirect({ ...paymentData, action: url }, {});
}

// Core change-package handler
async function doChangePackage(packageId, billingData, discountData = null) {
    progressMessage.value    = currentPlanId.value ? 'Processing your plan change...' : 'Setting up your subscription...';
    showProgressDialog.value = true;
    try {
        const payload = {
            packageId,
            ...(billingData || {}),
            ...(discountData ? {
                discountId:          discountData.discountId,
                discountedAmount:    discountData.discountedAmount,
                discountSavedAmount: discountData.discountSavedAmount,
            } : {}),
        };
        
        const res = currentPlanId.value 
            ? await subscriptionApi.changePackage(payload)
            : await subscriptionApi.subscribe(payload);
            
        const data = res?.data?.data;

        if (data?.requiresCheckout) {
            showProgressDialog.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, ts: Date.now() }));
            redirectToPayHere(data.checkoutData, true);
            return;
        }

        if (data?.requiresTokenization) {
            showProgressDialog.value = false;
            sessionStorage.setItem(PENDING_KEY, JSON.stringify({ packageId, ts: Date.now() }));
            redirectToPayHere(data.tokenizationData, true);
            return;
        }

        progressMessage.value = 'Waiting for subscription to update...';
        const finalResult = await waitForSubscriptionUpdate(packageId);
        showProgressDialog.value = false;

        if (!finalResult) {
            notifyError('Timeout', 'Plan update is taking longer than expected. Please refresh and try again.');
            return;
        }

        const { sub: finalSub, pending: finalPending } = finalResult;

        if (finalSub?.status === 'active') {
            currentPlanId.value   = finalSub.packageId?._id ?? finalSub.packageId;
            nextBillingDate.value = formatDate(finalSub.nextBillingDate);
        }

        if (pendingAction.value === 'downgrade') {
            pushView('downgradeSuccess');
        } else {
            if (finalPending) {
                notifyContrast('Scheduled', `Your plan change to ${finalPending.packageName} is scheduled for ${formatDate(finalPending.activationDate)}`);
            } else {
                notifyContrast('Success', `You're now on the ${pendingPackage.value.name} plan!`);
            }
            resetToMain();
        }
    } catch (err) {
        showProgressDialog.value = false;
        if (notifyIfManagedByInstitute(err)) {
            managedByInstitute.value = true;
            resetToMain();
            return;
        }
        // 409 — enrolments exceed the target plan and must be reduced first
        if (showBlockersFrom(err)) return;
        const msg = err?.response?.data?.message || '';
        notifyError('Error', msg || 'Failed to change plan. Please try again.');
    }
}

// Enrolments can't be locked the way notices can — the student on the other side is a paying user
// who made no change — so an over-limit switch is refused and the tutor resolves it first.
function showBlockersFrom(err) {
    const blockers = err?.response?.data?.data?.blockers;
    if (err?.response?.status !== 409 || !blockers?.length) return false;
    blockerList.value = blockers;
    showBlockedDialog.value = true;
    resetToMain();
    return true;
}

// Checked before opening the confirm view so the tutor never reaches payment on a plan they
// cannot move to. The server repeats this check — the UI pass only makes the flow nicer.
async function isChangeBlocked(pkg) {
    if (!currentPlanId.value) return false; // first-time subscribe has nothing to outgrow
    try {
        const res = await subscriptionApi.changePreview(pkg.id);
        const data = res?.data?.data;
        if (data?.allowed === false && data.blockers?.length) {
            blockedPackageName.value = data.packageName || pkg.name;
            blockerList.value = data.blockers;
            showBlockedDialog.value = true;
            return true;
        }
    } catch {
        // Advisory only — if the preview fails, let the server decide on submit.
    }
    return false;
}

// Button action routing
const handlePackageAction = async (event) => {
    const pkg = event.package;
    if (currentPlanId.value === pkg.id) return;
    if (await isChangeBlocked(pkg)) return;
    if (pendingChange.value && pendingChange.value.packageId === pkg.id) {
        notifyContrast('Plan Scheduled', `This plan is already scheduled to be activated on  ${formatDate(pendingChange.value.activationDate)}`);
        return;
    }

    if (event.action === 'upgrade')   pushView('upgradeConfirm',   { package: event.package });
    else if (event.action === 'downgrade') pushView('downgradeConfirm', { package: event.package });
};

// Called when user confirms in UpgradeConfirm or DowngradeConfirm
async function handleConfirmChange(action, eventData = null) {
    pendingPackage.value  = currentView.value.data.package;
    pendingAction.value   = action;
    pendingDiscount.value = eventData?.discount ?? null;
    actionLoading.value   = true;
    try {
        cachedHasToken.value = await checkHasToken();
        if (cachedHasToken.value) {
            await doChangePackage(pendingPackage.value.id, null, pendingDiscount.value);
        } else {
            showBillingDialog.value = true;
        }
    } catch {
        notifyError('Error', 'Something went wrong. Please try again.');
    } finally {
        actionLoading.value = false;
    }
}

// Called after billing form submitted
async function submitBilling(formData) {
    showBillingDialog.value = false;
    await doChangePackage(pendingPackage.value.id, formData, pendingDiscount.value);
}

// On mount: load data and check for PayHere return
async function checkPendingChange() {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return;

    let pending;
    try { pending = JSON.parse(raw); } catch {
        sessionStorage.removeItem(PENDING_KEY);
        return;
    }

    if (!pending?.packageId) {
        sessionStorage.removeItem(PENDING_KEY);
        return;
    }

    const isSuccess = !!sessionStorage.getItem('tutorSettingsPaymentSuccess');
    sessionStorage.removeItem('tutorSettingsPaymentSuccess');

    if (!isSuccess) {
        sessionStorage.removeItem(PENDING_KEY);
        subscriptionApi.cancelPending().catch(() => {});
        return;
    }

    progressMessage.value    = 'Activating your subscription, please wait...';
    showProgressDialog.value = true;

    const activeResult = await waitForSubscriptionUpdate(pending.packageId);
    showProgressDialog.value = false;
    sessionStorage.removeItem(PENDING_KEY);

    if (activeResult) {
        const { sub, pending: pChange } = activeResult;
        if (sub?.status === 'active') {
            currentPlanId.value   = sub.packageId?._id ?? sub.packageId;
            nextBillingDate.value = formatDate(sub.nextBillingDate);
        }
        if (pChange) {
            notifyContrast('Scheduled', `Your plan change to ${pChange.packageName} is scheduled for ${formatDate(pChange.activationDate)}`);
        } else {
            notifyContrast('Success', 'Your subscription has been updated!');
        }
    } else {
        notifyError('Timeout', 'Subscription activation is taking longer than expected. Please refresh and try again.');
        const sub = await fetchSubscription();
        if (sub?.status === 'active') {
            currentPlanId.value   = sub.packageId?._id ?? sub.packageId;
            nextBillingDate.value = formatDate(sub.nextBillingDate);
        }
    }
}

onMounted(async () => {
    pageLoading.value = true;
    checkHasToken().then(v => { cachedHasToken.value = v; }).catch(() => {});
    try {
        await fetchPackages();

        const hasPending = !!sessionStorage.getItem(PENDING_KEY);

        if (hasPending) {
            await checkPendingChange();
        } else {
            try {
                const sub = await fetchSubscription();
                if (sub?.status === 'active') {
                    currentPlanId.value   = sub.packageId?._id ?? sub.packageId;
                    nextBillingDate.value = formatDate(sub.nextBillingDate);
                }
            } catch { /* no subscription yet */ }
        }
    } finally {
        pageLoading.value = false;
    }
});
</script>

<style scoped>
.managed-badge {
    max-width: 520px;
    background-color: var(--surface-100);
    border: 1px solid var(--surface-border);
    color: var(--text-color);
}
.managed-badge .pi-verified {
    color: var(--teal-500);
}
</style>
